import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ChevronRight, ShoppingCart, Filter, X, ChevronDown, Loader2, Package, Zap, Award, Shield, Wrench, ArrowRight } from 'lucide-react';
import { cdnImage } from '../utils/cdn';
import ShopNavigation from '../components/shop/ShopNavigation';
import DualKonfigurator from '../components/shop/DualKonfigurator';
import Footer from '../components/shop/Footer';
import CookieConsent from '../components/shop/CookieConsent';
import { useAuth } from '../context/AuthContext';
import WishlistHeart from '../components/shop/WishlistHeart';
import RegisterCTA from '../components/shop/RegisterCTA';
import PromoBannerSlider from '../components/shop/PromoBannerSlider';
import '../styles/shop.css';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 24;
const HERO_IMG_PERFORMANCE = 'https://static.prod-images.emergentagent.com/jobs/6c0807fa-1806-4b29-b1b7-1e8672a8d1ef/images/3ff7aeecced85141627e1b9010822ca2f9c7d03e2be842275b522326697807df.png';
const HERO_IMG_FAHRWERK = 'https://static.prod-images.emergentagent.com/jobs/0c602028-af58-4d5e-9799-75228dec3ab8/images/034c009288f5161d3382649677e4d724f17e54412f150266d4464573c60f69e0.png';

function formatPrice(price) {
  if (price == null) return '0,00 €';
  return price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function ShopStatsBar({ total }) {
  return (
    <div className="shop-stats-bar" data-testid="shop-stats-bar">
      <div className="stat-item">
        <div className="stat-icon"><Package size={18} /></div>
        <div className="stat-text">
          <span className="stat-number">{total > 0 ? total.toLocaleString('de-DE') : '20.000+'}</span>
          <span className="stat-label">Performance-Teile</span>
        </div>
      </div>
      <div className="stat-item">
        <div className="stat-icon"><Award size={18} /></div>
        <div className="stat-text">
          <span className="stat-number">50+</span>
          <span className="stat-label">Premium-Marken</span>
        </div>
      </div>
      <div className="stat-item">
        <div className="stat-icon"><Shield size={18} /></div>
        <div className="stat-text">
          <span className="stat-number">Sichere Zahlung</span>
          <span className="stat-label">PayPal, Klarna & mehr</span>
        </div>
      </div>
      <div className="stat-item">
        <div className="stat-icon"><Wrench size={18} /></div>
        <div className="stat-text">
          <span className="stat-number">Profi</span>
          <span className="stat-label">Persönliche Beratung</span>
        </div>
      </div>
    </div>
  );
}

function FeaturedProductCard({ product }) {
  const imgSrc = cdnImage(product.image_url) || '/images/placeholder.svg';
  const cardRef = useRef(null);
  return (
    <Link to={`/p/${product.slug}`} className="product-card featured-card" data-testid={`featured-${product.alias}`}
      ref={cardRef}
      onClick={() => {
        sessionStorage.setItem('shopScrollY', window.scrollY.toString());
        sessionStorage.setItem('shopPage', (document.querySelector('[data-shoppage]')?.dataset.shoppage || '1'));
      }}>
      <div className="product-card-accent" />
      <div className="product-card-img">
        <img src={imgSrc} alt={product.name} loading="lazy" onError={e => { e.target.src = '/images/placeholder.svg'; }} />
        <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 3 }}>
          <WishlistHeart alias={product.alias} size={18} />
        </div>
      </div>
      <div className="product-card-info">
        <span className="featured-tag"><Zap size={10} /> Empfehlung</span>
        {product.manufacturer && <span className="product-brand">{product.manufacturer}</span>}
        <h3 className="product-name">{product.name}</h3>
        {product.variant_display && !(product.name || '').includes(product.variant_display) && (
          <span className="product-variant" data-testid="featured-card-variant">
            Variante: {product.variant_display}
          </span>
        )}
        <div className="product-price-row">
          <span className="product-price">{formatPrice(product.price)}</span>
          {product.compare_price > 0 && product.compare_price !== product.price && (
            <span className="product-compare-price">{formatPrice(product.compare_price)}</span>
          )}
        </div>
        {(product.delivery_time || product.stock > 0) && (
          <span className="product-stock-badge" style={{ marginTop: 12 }}>
            <Package size={12} /> {product.delivery_time || 'Auf Lager'}
          </span>
        )}
      </div>
    </Link>
  );
}

function ContentBreak() {
  return (
    <div className="grid-content-break" data-testid="content-break">
      <div className="content-break-card">
        <div className="content-break-icon"><Wrench size={22} /></div>
        <div className="content-break-title">Profi-Beratung</div>
        <div className="content-break-text">Unser Team berät dich persönlich zu jedem Fahrzeug und jedem Teil. Kostenlos und unverbindlich.</div>
      </div>
      <div className="content-break-card">
        <div className="content-break-icon"><Award size={22} /></div>
        <div className="content-break-title">Nur Originalteile</div>
        <div className="content-break-text">Wir führen ausschließlich Markenware von Akrapovic, Eventuri, Wagner Tuning, Maxton Design, Remus, Hurricane, HG-Motorsport, TA Technix, Novitec, APR und vielen weiteren Top-Herstellern.</div>
      </div>
      <div className="content-break-card">
        <div className="content-break-icon"><Shield size={22} /></div>
        <div className="content-break-title">Sichere Zahlungsarten</div>
        <div className="content-break-text">Bezahle bequem und sicher per PayPal, Klarna Ratenkauf, Kreditkarte, Sofortüberweisung oder Vorkasse. Käuferschutz inklusive.</div>
      </div>
    </div>
  );
}

function ProductCard({ product, index }) {
  const imgSrc = cdnImage(product.image_url) || '/images/placeholder.svg';
  const inStock = product.stock > 0 || product.delivery_time;
  const cardRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -4;
    const rotateY = ((x - centerX) / centerX) * 4;
    card.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-8px) scale(1.02)`;
  }, []);

  const handleMouseLeave = useCallback(() => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform = '';
  }, []);

  return (
    <Link to={`/p/${product.slug}`} className="product-card" data-testid={`product-${product.alias}`}
      data-reveal
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ animationDelay: `${(index % 8) * 0.06}s` }}
      onClick={() => {
        sessionStorage.setItem('shopScrollY', window.scrollY.toString());
        sessionStorage.setItem('shopPage', (document.querySelector('[data-shoppage]')?.dataset.shoppage || '1'));
      }}>
      <div className="product-card-accent" />
      <div className="product-card-img">
        <img src={imgSrc} alt={product.name} loading="lazy" onError={e => { e.target.src = '/images/placeholder.svg'; }} />
        {product.is_new && <span className="badge-new">NEU</span>}
        {product.is_on_sale && <span className="badge-sale">SALE</span>}
        {product.compare_price > 0 && product.compare_price > product.price && (
          <span className="badge-sale">-{Math.round((1 - product.price / product.compare_price) * 100)}%</span>
        )}
        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 3 }}>
          <WishlistHeart alias={product.alias} size={16} />
        </div>
      </div>
      <div className="product-card-info">
        {product.manufacturer && <span className="product-brand">{product.manufacturer}</span>}
        <h3 className="product-name">{product.name}</h3>
        {product.variant_display && !(product.name || '').includes(product.variant_display) && (
          <span className="product-variant" data-testid="product-card-variant">
            Variante: {product.variant_display}
          </span>
        )}
        <div className="product-price-row">
          <span className="product-price">{formatPrice(product.price)}</span>
          {product.compare_price > 0 && product.compare_price !== product.price && (
            <span className="product-compare-price">{formatPrice(product.compare_price)}</span>
          )}
        </div>
        {inStock && (
          <span className="product-stock-badge">
            <Package size={11} />
            {product.delivery_time || 'Auf Lager'}
          </span>
        )}
      </div>
    </Link>
  );
}

export default function ProductListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || '';
  const search = searchParams.get('q') || '';
  const mfr = searchParams.get('manufacturer') || '';
  const sortBy = searchParams.get('sort') || 'position';
  const engineFilter = searchParams.get('engine') || '';
  const powerKwFilter = searchParams.get('power_kw') || '';
  const { token } = useAuth();

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryPath, setCategoryPath] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [searchInput, setSearchInput] = useState(search);
  const [konfigTab, setKonfigTab] = useState('performance');
  const [catFilters, setCatFilters] = useState({ engines: [], powers_kw: [], enabled: false });
  const filterKey = `${category}|${search}|${mfr}|${sortBy}|${engineFilter}|${powerKwFilter}`;
  const prevFilterKey = useRef(filterKey);
  const restoringRef = useRef(false);
  const pendingScrollRef = useRef(null);

  const fetchPage = useCallback(async (pageNum) => {
    let url;
    if (search) {
      url = `${API}/api/shop/search?q=${encodeURIComponent(search)}&page=${pageNum}&limit=${PAGE_SIZE}`;
    } else {
      const params = new URLSearchParams({ page: pageNum, limit: PAGE_SIZE, sort_by: sortBy });
      if (category) params.set('category', category);
      if (mfr) params.set('manufacturer', mfr);
      if (engineFilter) params.set('engine', engineFilter);
      if (powerKwFilter) params.set('power_kw', powerKwFilter);
      url = `${API}/api/shop/products?${params}`;
    }
    // v242a: defensive JSON parsing - Cloudflare/Proxy kann leere/HTML-Antworten liefern
    try {
      const res = await fetch(url);
      const txt = await res.text();
      if (!txt) return { products: [], total: 0 };
      const data = JSON.parse(txt);
      return { products: data.products || [], total: data.total || 0 };
    } catch (e) {
      console.warn('[shop] product fetch failed:', e?.message || e);
      return { products: [], total: 0 };
    }
  }, [category, search, mfr, sortBy, engineFilter, powerKwFilter]);

  // Disable browser's automatic scroll restoration (we handle it ourselves)
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Restore all pages + scroll position when coming back from product detail
  useEffect(() => {
    // Track search query server-side
    if (search && token) {
      fetch(`${API}/api/auth/me/search-history`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: search })
      }).catch(() => {});
    }

    const savedY = sessionStorage.getItem('shopScrollY');
    const savedPage = sessionStorage.getItem('shopPage');

    const loadData = async () => {
      const targetPage = savedY && savedPage ? parseInt(savedPage, 10) : 1;

      if (targetPage > 1) {
        restoringRef.current = true;
        setLoading(true);
        let allProducts = [];
        for (let p = 1; p <= targetPage; p++) {
          const data = await fetchPage(p);
          allProducts = [...allProducts, ...data.products];
          if (p === 1) setTotal(data.total);
        }
        setProducts(allProducts);
        setPage(targetPage);
        setLoading(false);
        pendingScrollRef.current = parseInt(savedY, 10);
      } else {
        setLoading(true);
        const data = await fetchPage(1);
        setProducts(data.products);
        setTotal(data.total);
        setPage(1);
        setLoading(false);
        if (savedY) pendingScrollRef.current = parseInt(savedY, 10);
      }

      sessionStorage.removeItem('shopScrollY');
      sessionStorage.removeItem('shopPage');
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Execute pending scroll after products are rendered
  useEffect(() => {
    if (!loading && pendingScrollRef.current !== null) {
      const scrollY = pendingScrollRef.current;
      pendingScrollRef.current = null;
      restoringRef.current = false;

      // Force all data-reveal cards to be visible immediately (skip animation)
      if (gridRef.current) {
        gridRef.current.querySelectorAll('[data-reveal]').forEach(card => {
          card.classList.add('revealed');
          card.style.animation = 'none';
          card.style.opacity = '1';
          card.style.transform = 'none';
        });
      }

      // Retry scrolling until the page is tall enough or max attempts
      let attempts = 0;
      const tryScroll = () => {
        attempts++;
        window.scrollTo(0, scrollY);
        if (Math.abs(window.scrollY - scrollY) > 50 && attempts < 20) {
          setTimeout(tryScroll, 50);
        }
      };
      // Wait for DOM to settle, then scroll
      setTimeout(tryScroll, 100);
    }
  }, [loading, products]);

  // Reset and fetch page 1 when filters change (not on initial mount)
  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      setPage(1);
      setProducts([]);
      sessionStorage.removeItem('shopScrollY');
      sessionStorage.removeItem('shopPage');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setLoading(true);
      fetchPage(1).then(data => {
        setProducts(data.products);
        setTotal(data.total);
        setLoading(false);
      });
    }
  }, [filterKey, fetchPage]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    setLoadingMore(true);
    fetchPage(nextPage).then(data => {
      setProducts(prev => [...prev, ...data.products]);
      setLoadingMore(false);
    });
  };

  const hasMore = products.length < total;

  useEffect(() => {
    fetch(`${API}/api/shop/categories/tree`).then(r => r.ok ? r.json() : null).then(d => d && setCategories(d.tree || [])).catch(() => {});
    fetch(`${API}/api/shop/manufacturers`).then(r => r.ok ? r.json() : null).then(d => d && setManufacturers(d.manufacturers || [])).catch(() => {});
  }, []);

  // Build a flat path->name lookup from the category tree
  const buildNameMap = useCallback((tree) => {
    const map = {};
    const traverse = (nodes) => {
      for (const node of nodes) {
        if (node.path && node.name) map[node.path] = node.name;
        if (node.children) traverse(node.children);
      }
    };
    traverse(tree);
    return map;
  }, []);

  // Build breadcrumb from category path + fetch SEO data
  useEffect(() => {
    if (category) {
      const parts = category.split('/');
      const catNameMap = buildNameMap(categories);
      const path = parts.map((p, i) => {
        const fullPath = parts.slice(0, i + 1).join('/');
        return {
          name: catNameMap[fullPath] || p.replace(/__/g, ' / ').replace(/_/g, ' '),
          value: fullPath,
        };
      });
      setCategoryPath(path);
      const catName = path.length > 0 ? path[path.length - 1].name : '';

      // Fetch custom SEO data from DB, fallback to default
      fetch(`${API}/api/shop/category-seo/${encodeURIComponent(category)}`)
        .then(r => r.ok ? r.json() : null)
        .then(seo => {
          if (!seo) return;
          document.title = seo.seo_title || (catName ? `${catName} | TrXL Performance` : 'Shop | TrXL Performance');
          let meta = document.querySelector('meta[name="description"]');
          if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
          meta.content = seo.seo_description || (catName ? `${catName} - Top-Produkte bei TrXL Performance. Tuning, Chiptuning & Performance-Teile.` : 'TrXL Performance Shop - Tuning, Chiptuning & Performance-Teile.');
        })
        .catch(() => {
          document.title = catName ? `${catName} | TrXL Performance` : 'Shop | TrXL Performance';
        });

      // Fetch SEO content for bottom section
      fetch(`${API}/api/shop/category-seo-content?category=${encodeURIComponent(category)}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => setSeoContent(data))
        .catch(() => setSeoContent(null));
    } else {
      setCategoryPath([]);
      setSeoContent(null);
      document.title = search ? `Suche: ${search} | TrXL Performance` : 'Shop | TrXL Performance';
    }
    return () => { document.title = 'TrXL Performance'; };
  }, [category, search, categories, buildNameMap]);

  // Fetch dynamic category-filters (engine + power_kw) for Sportauspuff categories
  useEffect(() => {
    if (!category) {
      setCatFilters({ engines: [], powers_kw: [], enabled: false });
      return;
    }
    fetch(`${API}/api/shop/category-filters?category=${encodeURIComponent(category)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) setCatFilters({ engines: d.engines || [], powers_kw: d.powers_kw || [], enabled: !!d.enabled });
        else setCatFilters({ engines: [], powers_kw: [], enabled: false });
      })
      .catch(() => setCatFilters({ engines: [], powers_kw: [], enabled: false }));
  }, [category]);

  const [seoContent, setSeoContent] = useState(null);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setSearchParams({ q: searchInput.trim() });
    }
  };

  const setParam = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    setSearchParams(params);
  };

  // Helpers for multi-value filter strings (comma-separated)
  const toggleListValue = (csv, value) => {
    const arr = (csv || '').split(',').map(s => s.trim()).filter(Boolean);
    const idx = arr.indexOf(value);
    if (idx >= 0) arr.splice(idx, 1); else arr.push(value);
    return arr;
  };
  const toggleListValueStr = (csv, value) => toggleListValue(csv, value).join(',');

  const pageTitle = search ? `Suche: "${search}"` : categoryPath.length > 0 ? categoryPath[categoryPath.length - 1].name : 'Alle Produkte';

  // Scroll Reveal Observer
  const gridRef = useRef(null);
  useEffect(() => {
    if (loading || !gridRef.current) return;
    const cards = gridRef.current.querySelectorAll('[data-reveal]');
    if (!cards.length) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      cards.forEach(c => c.classList.add('revealed'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    cards.forEach(card => observer.observe(card));
    return () => observer.disconnect();
  }, [loading, products]);

  return (
    <div className="shop-page" data-testid="product-list-page">
      <ShopNavigation hideKonfigurator />

      {/* Hero Banner with Konfigurator */}
      <div className={`shop-hero ${konfigTab === 'fahrwerk' ? 'shop-hero-fahrwerk' : ''}`} data-testid="shop-hero">
        <img
          src={konfigTab === 'fahrwerk' ? HERO_IMG_FAHRWERK : HERO_IMG_PERFORMANCE}
          alt="" className="shop-hero-bg"
          key={konfigTab}
        />
        <div className="shop-hero-overlay" />
        <div className="shop-hero-content">
          <div className="shop-hero-text">
            <span className="shop-hero-tag">
              {konfigTab === 'fahrwerk' ? 'FAHRWERKTECHNIK' : 'PERFORMANCE PARTS & STYLING'}
            </span>
            <h2 className="shop-hero-title">
              {konfigTab === 'fahrwerk'
                ? <>Das perfekte Fahrwerk<br/>für dein Fahrzeug</>
                : <>Finde das passende Teil<br/>für dein Fahrzeug</>
              }
            </h2>
          </div>
          <div className="shop-hero-konfig">
            <DualKonfigurator onTabChange={setKonfigTab} />
          </div>
        </div>
      </div>

      {/* Red accent divider */}
      <div className="shop-divider" />

      {/* Scroll hint arrow when category is selected */}
      {category && !loading && products.length > 0 && (
        <div className="scroll-hint" data-testid="scroll-hint" onClick={() => {
          const el = document.querySelector('[data-testid="featured-row"], [data-testid="product-grid"]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}>
          <span>{total} Produkte gefunden</span>
          <ChevronDown size={20} className="scroll-hint-arrow" />
        </div>
      )}

      {/* Premium Stats Bar */}
      <ShopStatsBar total={total} />

      {/* Aktions-Banner-Slider (Aktionen + Gewinnspiel/Lose) */}
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 24px' }}>
        <PromoBannerSlider />
      </div>

      <div className="shop-content">
        {/* Sidebar */}
        <aside className={`shop-sidebar ${showFilters ? 'open' : ''}`} data-testid="shop-sidebar">
          <div className="sidebar-header-mobile">
            <h3>Filter</h3>
            <button onClick={() => setShowFilters(false)}><X size={20} /></button>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="sidebar-search">
            <Search size={16} />
            <input type="text" placeholder="Produkte suchen..." value={searchInput}
              onChange={e => setSearchInput(e.target.value)} data-testid="search-input" />
          </form>

          {/* Category Navigation */}
          <div className="sidebar-section">
            <h4>Fahrzeuge & Kategorien</h4>
            <div className="cat-nav" data-testid="category-nav">
              {category && (
                <button className="cat-back" onClick={() => {
                  const parts = category.split('/');
                  parts.pop();
                  setParam('category', parts.join('/'));
                }}>
                  <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Zurück
                </button>
              )}
              {(() => {
                // Show subcategories of current category, or top-level if no category selected
                if (!category) {
                  return categories.map(c => (
                    <button key={c.alias} className="cat-item-link"
                      onClick={() => setParam('category', c.alias)}>
                      {c.name} {c.children?.length > 0 && <ChevronRight size={14} />}
                    </button>
                  ));
                }
                // Find children of current category in the tree
                const parts = category.split('/');
                let current = categories;
                let found = null;
                for (const part of parts) {
                  found = current.find(c => c.alias === part);
                  if (!found) break;
                  current = found.children || [];
                }
                if (current && current.length > 0) {
                  return current.map(c => (
                    <button key={c.alias} className={`cat-item-link ${category === (category + '/' + c.alias) ? 'active' : ''}`}
                      onClick={() => setParam('category', category + '/' + c.alias)}>
                      {c.name} {c.children?.length > 0 && <ChevronRight size={14} />}
                    </button>
                  ));
                }
                return <p style={{ color: '#888', fontSize: '0.85rem' }}>Keine Unterkategorien</p>;
              })()}
            </div>
          </div>

          {/* Manufacturer Filter */}
          <div className="sidebar-section">
            <h4>Hersteller</h4>
            <div className="mfr-list">
              {manufacturers.slice(0, 20).map(m => (
                <button key={m.name} className={`mfr-item ${mfr === m.name ? 'active' : ''}`}
                  onClick={() => setParam('manufacturer', mfr === m.name ? '' : m.name)}>
                  {m.name} <span className="mfr-count">({m.count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Motor + Leistung Filter (nur Sportauspuff-Kategorien, dynamisch) */}
          {catFilters.enabled && (catFilters.engines.length > 0 || catFilters.powers_kw.length > 0) && (
            <>
              {catFilters.engines.length > 0 && (
                <div className="sidebar-section" data-testid="filter-engine">
                  <h4>Motor</h4>
                  <div className="mfr-list">
                    {catFilters.engines.map(e => {
                      const active = toggleListValue(engineFilter, e.value).includes(e.value)
                        && engineFilter.split(',').map(s => s.trim()).includes(e.value);
                      return (
                        <button key={e.value}
                          className={`mfr-item ${active ? 'active' : ''}`}
                          data-testid={`filter-engine-${e.value.replace(/\s+/g, '-').toLowerCase()}`}
                          onClick={() => setParam('engine', toggleListValueStr(engineFilter, e.value))}>
                          {e.label} <span className="mfr-count">({e.count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {catFilters.powers_kw.length > 0 && (
                <div className="sidebar-section" data-testid="filter-power">
                  <h4>Leistung</h4>
                  <div className="mfr-list">
                    {catFilters.powers_kw.map(p => {
                      const active = powerKwFilter.split(',').map(s => s.trim()).includes(p.value);
                      return (
                        <button key={p.value}
                          className={`mfr-item ${active ? 'active' : ''}`}
                          data-testid={`filter-power-${p.value}kw`}
                          onClick={() => setParam('power_kw', toggleListValueStr(powerKwFilter, p.value))}>
                          {p.label} <span className="mfr-count">({p.count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {(engineFilter || powerKwFilter) && (
                <button className="cat-back" data-testid="reset-vehicle-filters"
                  style={{ marginTop: 4 }}
                  onClick={() => {
                    const p = new URLSearchParams(searchParams);
                    p.delete('engine'); p.delete('power_kw');
                    setSearchParams(p);
                  }}>
                  Motor-Filter zurücksetzen
                </button>
              )}
            </>
          )}
        </aside>

        {/* Main Content */}
        <main className="shop-main">
          {/* Breadcrumb */}
          <nav className="breadcrumb" data-testid="breadcrumb">
            <Link to="/">Start</Link>
            <ChevronRight size={12} />
            <Link to="/shop">Shop</Link>
            {categoryPath.map((c, i) => (
              <React.Fragment key={c.value}>
                <ChevronRight size={12} />
                {i === categoryPath.length - 1 ? (
                  <span>{c.name}</span>
                ) : (
                  <button onClick={() => setParam('category', c.value)}>{c.name}</button>
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* Header */}
          <div className="shop-header">
            <div>
              <h1 data-testid="page-title">{pageTitle}</h1>
              <p className="result-count">
                {total.toLocaleString('de-DE')} Produkte
                {(engineFilter || powerKwFilter) && (
                  <span style={{ color: '#EC2229', marginLeft: 8 }} data-testid="active-vehicle-filters">
                    · gefiltert: {[engineFilter, powerKwFilter ? `${powerKwFilter} kW` : ''].filter(Boolean).join(' · ')}
                  </span>
                )}
              </p>
            </div>
            <div className="shop-controls">
              <button className="filter-toggle" onClick={() => setShowFilters(true)} data-testid="filter-toggle">
                <Filter size={16} /> Filter
              </button>
              <div className="sort-select">
                <select value={sortBy} onChange={e => setParam('sort', e.target.value)} data-testid="sort-select">
                  <option value="position">Standard (Position)</option>
                  <option value="name_asc">Name A-Z</option>
                  <option value="name_desc">Name Z-A</option>
                  <option value="alias_asc">Artikelnr. aufsteigend</option>
                  <option value="alias_desc">Artikelnr. absteigend</option>
                  <option value="price_asc">Preis aufsteigend</option>
                  <option value="price_desc">Preis absteigend</option>
                  <option value="newest">Neueste zuerst</option>
                  <option value="oldest">Älteste zuerst</option>
                  <option value="bestseller">Bestseller</option>
                </select>
                <ChevronDown size={14} />
              </div>
            </div>
          </div>

          {/* Product Grid */}
          {loading ? (
            <div className="loading-grid">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="product-skeleton" style={{ animationDelay: `${i * 0.08}s` }} />
              ))}
            </div>
          ) : products.length > 0 ? (
            <>
              {/* Featured Products (first page only, first 2 products) */}
              {page === 1 && products.length >= 4 && !search && (
                <div className="featured-products-row" data-testid="featured-row">
                  <FeaturedProductCard product={products[0]} />
                  <FeaturedProductCard product={products[1]} />
                </div>
              )}

              <RegisterCTA variant="inline" />

              {/* Section heading */}
              {page === 1 && products.length >= 4 && !search && (
                <div className="section-heading"><h2>Alle Produkte</h2></div>
              )}

              <div className="product-grid" data-testid="product-grid" data-shoppage={page} ref={gridRef}>
                {/* Skip first 2 if showing featured */}
                {(page === 1 && products.length >= 4 && !search ? products.slice(2) : products).map((p, idx) => {
                  return <ProductCard key={`${p.alias}-${idx}`} product={p} index={idx} />;
                })}
              </div>
            </>
          ) : (
            <div className="no-results" data-testid="no-results">
              <p>Keine Produkte gefunden.</p>
              <Link to="/shop">Alle Produkte anzeigen</Link>
            </div>
          )}

          {/* Load More */}
          {!loading && hasMore && (
            <div className="load-more-wrap" data-testid="load-more-wrap">
              <p className="load-more-count">{products.length} von {total.toLocaleString('de-DE')} Produkten angezeigt</p>
              <button className="load-more-btn" onClick={handleLoadMore} disabled={loadingMore} data-testid="load-more-btn">
                {loadingMore ? (
                  <><Loader2 size={18} className="spin" /> Laden...</>
                ) : (
                  'Weitere Produkte laden'
                )}
              </button>
            </div>
          )}
          {!loading && !hasMore && products.length > 0 && (
            <p className="load-more-count" style={{ textAlign: 'center', padding: '2rem 0' }}>
              Alle {total.toLocaleString('de-DE')} Produkte angezeigt
            </p>
          )}
        </main>
      </div>

      {/* Content Break - Moved below products, before SEO section */}
      <ContentBreak />

      {/* Dynamic SEO Content Section */}
      {seoContent && category && !search && (
        <div className="seo-content-section" data-testid="seo-content">
          <div className="seo-content-inner">
            <h2 className="seo-title">{seoContent.title}</h2>
            <p className="seo-intro">{seoContent.intro}</p>

            <div className="seo-grid">
              <div className="seo-block">
                <h3>Warum TrXL Performance?</h3>
                <p>{seoContent.why_section}</p>
              </div>
              {seoContent.brands_section && (
                <div className="seo-block">
                  <h3>Top-Marken in dieser Kategorie</h3>
                  <p>{seoContent.brands_section}</p>
                  {seoContent.brands.length > 0 && (
                    <div className="seo-brand-tags">
                      {seoContent.brands.slice(0, 12).map(b => (
                        <Link key={b} to={`/shop?manufacturer=${encodeURIComponent(b)}`} className="seo-brand-tag">{b}</Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {seoContent.subcategory_links.length > 0 && (
              <div className="seo-links-section">
                <h3>Weitere Kategorien entdecken</h3>
                <div className="seo-links-grid">
                  {seoContent.subcategory_links.map(link => (
                    <Link key={link.path} to={`/shop?category=${encodeURIComponent(link.path)}`} className="seo-link-item">
                      <ArrowRight size={12} /> {link.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <p className="seo-cta">{seoContent.cta}</p>
          </div>
        </div>
      )}

      <Footer />
      <CookieConsent />
    </div>
  );
}
